/* cart-toast.js — "Added to cart" toast for Ninja Patches (desktop + mobile).
   Ported from the Ninja Transfers mobile toast; adapted for Patches where the
   slideout cart is killed (settings.cart_action = "no-overlay") on BOTH
   breakpoints, so the toast must render on desktop AND mobile.

   window.showCartToast(addData) — addData is the /cart/add.js response (a single
   line-item object, or { items:[...] } for multi-variant adds). Pass the DELTA,
   never a full /cart.js / ?view=ajax cart (those report the whole cart, not what
   was just added).

   Thumbnail = the line item's own image: properties._cartImg (custom patches'
   uploaded design preview) || properties._Upload || featured_image. Each shown
   toast also persists a tiny payload to sessionStorage so the reload-based custom
   flows (?np_added / ?rtype=another) can replay it via window.npReplayCartToast()
   — see custom-uploader.js. */
(function () {
  var el, timer, styleInjected;

  /* ── Styles ─────────────────────────────────────────────────── */

  function injectStyles() {
    if (styleInjected) return;
    styleInjected = true;
    var s = document.createElement('style');
    s.id = 'atc-toast-css';
    s.textContent = [
      /* Mobile (default): bottom-anchored, centered, capped at 360px. */
      /* Design-HTML tokens (source of truth). --atc-dur MUST stay equal to the JS dismiss
         setTimeout (5000ms) so the .atc-cta__bar countdown finishes exactly as the toast hides. */
      '.atc-toast{--atc-cta:#63AF62;--atc-load:#4F934E;--atc-dur:5s;--atc-field:#cffca6;--atc-green:#098605;--atc-ink:#0b5e04;--nt-font-body:\'Inter\',-apple-system,system-ui,\'Segoe UI\',Roboto,sans-serif;position:fixed;bottom:calc(env(safe-area-inset-bottom,0px) + 16px);left:12px;right:12px;max-width:360px;margin-inline:auto;z-index:2147483647;background:#fff;border-radius:18px;box-shadow:0 4px 24px rgba(0,0,0,.12);opacity:0;transform:translateY(20px);transition:opacity .2s ease,transform .2s ease;pointer-events:none;overflow:hidden;font-family:var(--nt-font-body);}',
      '.atc-toast.is-visible{opacity:1;transform:translateY(0);pointer-events:auto;}',
      /* Desktop: top-right anchored, fixed ~390px, slides down from the top. */
      '@media (min-width:750px){.atc-toast{top:16px;right:16px;bottom:auto;left:auto;margin:0;width:390px;max-width:calc(100vw - 32px);transform:translateY(-20px);}}',
      '.atc-head{display:flex;align-items:center;gap:9px;background:var(--atc-field);padding:10px 12px 10px 14px;}',
      '.atc-check{width:20px;height:20px;border-radius:50%;background:#fff;display:flex;align-items:center;justify-content:center;flex-shrink:0;}',
      '.atc-check svg{width:12px;height:12px;}',
      '.atc-head__label{font-size:13.5px;font-weight:700;color:var(--atc-ink);letter-spacing:-0.01em;line-height:1;}',
      '.atc-close{margin-left:auto;background:none;border:none;padding:4px;cursor:pointer;color:#098605;display:flex;align-items:center;justify-content:center;flex-shrink:0;opacity:.7;}',
      '.atc-close:hover{opacity:1;}',
      '.atc-close svg{width:16px;height:16px;}',
      '.atc-body{padding:13px 16px 16px;}',
      '.atc-row{display:flex;align-items:center;gap:12px;}',
      '.atc-thumb{width:48px;height:48px;border-radius:10px;object-fit:contain;flex-shrink:0;background:#f2f2f2;}',
      '.atc-info{flex:1;min-width:0;}',
      '.atc-name{font-size:14px;font-weight:700;color:var(--color-text-main,#000);line-height:1.3;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}',
      '.atc-meta{display:flex;justify-content:space-between;align-items:baseline;gap:8px;margin-top:4px;}',
      '.atc-variant{font-size:13px;color:var(--color-secondary-text-main,rgba(0,0,0,.6));line-height:1.3;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}',
      '.atc-price{font-size:14px;font-weight:700;color:var(--color-text-main,#000);line-height:1.3;white-space:nowrap;flex-shrink:0;}',
      '.atc-cta{display:flex;align-items:center;justify-content:center;gap:7px;margin-top:12px;position:relative;overflow:hidden;isolation:isolate;width:100%;height:46px;background:var(--atc-cta);color:#fff;border:none;border-radius:12px;cursor:pointer;font-family:var(--nt-font-body);font-weight:700;font-size:15px;letter-spacing:-0.01em;text-decoration:none;}',
      '.atc-cta__label{position:relative;z-index:1;display:inline-flex;align-items:center;gap:7px;white-space:nowrap;}',
      '.atc-cta__bar{position:absolute;inset:0;z-index:0;background:var(--atc-load);transform-origin:left;transform:scaleX(0);pointer-events:none;animation:atc-countdown var(--atc-dur) linear forwards;}',
      '.atc-cta svg{width:14px;height:14px;flex-shrink:0;}',
      '@keyframes atc-countdown{from{transform:scaleX(0);}to{transform:scaleX(1);}}'
    ].join('\n');
    document.head.appendChild(s);
  }

  /* ── Normalize add response ─────────────────────────────────── */

  function normalizeAdd(addData) {
    if (!addData) return null;

    var items;
    if (Array.isArray(addData.items)) {
      items = addData.items;
    } else if (Array.isArray(addData)) {
      items = addData;
    } else if (addData.id) {
      items = [addData];
    } else {
      return null;
    }
    if (!items.length) return null;

    var first = items[0];
    var title = first.product_title || first.title || '';
    var rawVariant = first.variant_title || '';
    var variant = rawVariant.indexOf(' / ') !== -1
      ? rawVariant.split(' / ')[0]
      : rawVariant;
    if (variant) variant = variant.toLowerCase().replace(/\b[a-z]/g, function (c) { return c.toUpperCase(); });

    /* Thumbnail source, in priority order: the custom-patch uploaded design preview
       (properties._cartImg), then the raw uploaded file (properties._Upload), then
       the line item's own product image. Custom patches carry _cartImg/_Upload;
       standard products fall through to featured_image. */
    var props = first.properties || {};
    var image = props._cartImg
      || props._Upload
      || first.image
      || (first.featured_image && first.featured_image.url)
      || '';

    var totalQty = 0;
    var totalPrice = 0;
    for (var i = 0; i < items.length; i++) {
      totalQty += items[i].quantity || 1;
      totalPrice += items[i].final_line_price || items[i].line_price || items[i].price || 0;
    }
    return { title: title, variant: variant, qty: totalQty, price: totalPrice, image: image };
  }

  function formatMoney(cents) {
    if (typeof cents !== 'number') return '';
    var fmt = (typeof KROWN !== 'undefined' && KROWN.settings && KROWN.settings.shop_money_format) || '${{amount}}';
    var dollars = (cents / 100).toFixed(2);
    var result = fmt
      .replace('{{amount_with_comma_separator}}', dollars.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace('{{amount_no_decimals}}', Math.round(cents / 100).toString())
      .replace('{{amount}}', dollars);
    return result.replace(/<[^>]*>/g, '');
  }

  /* ── DOM construction ───────────────────────────────────────── */

  function getEl() {
    if (el && el.parentNode) return el;
    injectStyles();
    el = document.createElement('div');
    el.className = 'atc-toast';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('aria-atomic', 'true');
    el.innerHTML =
      '<div class="atc-head">' +
        '<span class="atc-check">' +
          '<svg viewBox="0 0 12 12" fill="none"><path d="M2.5 6.5L5 9l4.5-5" stroke="#098605" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '</span>' +
        '<span class="atc-head__label">Added to cart</span>' +
        '<button type="button" class="atc-close" aria-label="Dismiss">' +
          '<svg viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
        '</button>' +
      '</div>' +
      '<div class="atc-body">' +
        '<div class="atc-row">' +
          '<img class="atc-thumb" alt="" />' +
          '<div class="atc-info">' +
            '<div class="atc-name"></div>' +
            '<div class="atc-meta">' +
              '<span class="atc-variant"></span>' +
              '<span class="atc-price"></span>' +
            '</div>' +
          '</div>' +
        '</div>' +
        '<a href="/cart" class="atc-cta">' +
          '<span class="atc-cta__label">' +
            'View cart' +
            '<svg viewBox="0 0 14 14" fill="none"><path d="M1 7h12m0 0L8.5 2.5M13 7l-4.5 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
          '</span>' +
          '<span class="atc-cta__bar"></span>' +
        '</a>' +
      '</div>';

    el.querySelector('.atc-close').addEventListener('click', function (e) {
      e.stopPropagation();
      hide();
    });
    el.addEventListener('click', function (e) {
      if (e.target.closest('.atc-cta')) return;
      hide();
    });
    el.addEventListener('touchstart', pauseTimer, { passive: true });
    el.addEventListener('mouseenter', pauseTimer);
    el.addEventListener('touchend', resumeTimer, { passive: true });
    el.addEventListener('mouseleave', resumeTimer);
    document.body.appendChild(el);
    return el;
  }

  /* ── Timer / visibility ─────────────────────────────────────── */

  /* The .atc-cta__bar countdown is wired to the dismiss timer (both 5s). These keep them in
     step on the hover/touch pause: freeze the bar with the timer, restart it fresh on resume
     (matching resumeTimer's fresh 5s). Toast-internal only — does NOT touch the reload/replay flow. */
  function pauseBar() {
    if (!el) return;
    var bar = el.querySelector('.atc-cta__bar');
    if (bar) bar.style.animationPlayState = 'paused';
  }
  function restartBar() {
    if (!el) return;
    var bar = el.querySelector('.atc-cta__bar');
    if (!bar) return;
    bar.style.animation = 'none';
    void bar.offsetWidth;            // reflow → re-trigger the animation from scaleX(0)
    bar.style.animation = '';        // restore the CSS animation (atc-countdown var(--atc-dur))
    bar.style.animationPlayState = 'running';
  }

  function pauseTimer() {
    if (timer) { clearTimeout(timer); timer = null; }
    pauseBar();
  }

  function resumeTimer() {
    if (!el || !el.classList.contains('is-visible')) return;
    restartBar();
    timer = setTimeout(hide, 5000);
  }

  function hide() {
    if (!el) return;
    el.classList.remove('is-visible');
    if (timer) { clearTimeout(timer); timer = null; }
  }
  /* Exposed so the reload-bound custom flows can dismiss a LINGERING toast (e.g. the design-1
     "Added to cart" still visible on its 5s timer) the instant a second-upload/reload begins —
     otherwise it flashes above the loading spinner on the dying page. See custom-uploader.js. */
  window.hideCartToast = hide;

  /* Render a normalized payload {title, variant, qty, price, image} into the toast. */
  function show(data) {
    var t = getEl();
    var thumbEl = t.querySelector('.atc-thumb');

    t.querySelector('.atc-name').textContent = data.title;
    var variantParts = [];
    if (data.variant) variantParts.push(data.variant);
    variantParts.push('Qty ' + data.qty);
    t.querySelector('.atc-variant').textContent = variantParts.join(' · ');
    t.querySelector('.atc-price').textContent = formatMoney(data.price);

    if (data.image) { thumbEl.src = data.image; thumbEl.style.display = ''; }
    else { thumbEl.style.display = 'none'; }

    t.classList.remove('is-visible');
    void t.offsetWidth;
    t.classList.add('is-visible');
    restartBar();                    // start the CTA countdown bar fresh, in step with the dismiss timer
    if (timer) clearTimeout(timer);
    timer = setTimeout(hide, 5000);
  }

  /* ── Reload-survival stash (custom ?np_added / ?rtype=another flows) ──────
     Those flows re-render the page, so the just-added line can't be matched by
     position (Shopify merges identical lines IN PLACE — "last line" is unsafe).
     Instead each shown toast persists its exact payload here; the arrival replays
     it via npReplayCartToast(). Tiny on purpose: URL only (never a data-URI), with
     a short TTL + clear-on-read so a stale payload can't surface on an unrelated
     later ?np_added navigation. */
  var STASH_KEY = 'npCartToast';
  var STASH_TTL = 180000; // 3 min

  function persist(data) {
    try {
      var img = data.image || '';
      if (img.indexOf('data:') === 0 || img.length > 2000) img = ''; // URL only — never a data-URI
      sessionStorage.setItem(STASH_KEY, JSON.stringify({ t: data.title, v: data.variant, q: data.qty, p: data.price, i: img, ts: Date.now() }));
    } catch (e) {}
  }

  /* ── Public API ─────────────────────────────────────────────── */

  window.showCartToast = function (addData) {
    var data = normalizeAdd(addData);
    if (!data) return;
    persist(data);
    /* DO-NOT-REVERT (Patches cart toast, blank-reload fix): the custom add-another flows reload
       the PDP (?np_added / ?rtype) to restart with the new design. Persist the stash (above) but
       SUPPRESS the pre-reload toast — the post-reload single owner replays it once, so it no
       longer flashes on the dying page. Both reload-pending markers (set before the add):
       __npSecondUploadInProgress (new-file second upload → ?np_added) and anotherTransfer (the
       .anotherTransfer / ?rtype path). See .claude/rules/patches-cart-toast.md */
    if (window.__npSecondUploadInProgress || window.anotherTransfer) return;
    show(data);
  };

  /* Replay the stashed payload after a reload-based custom add. Returns true if a
     fresh stash was shown. Clears the stash on read (even when stale); ignores
     anything older than the TTL. */
  window.npReplayCartToast = function () {
    var raw;
    try { raw = sessionStorage.getItem(STASH_KEY); sessionStorage.removeItem(STASH_KEY); } catch (e) { return false; }
    if (!raw) return false;
    var s;
    try { s = JSON.parse(raw); } catch (e) { return false; }
    if (!s || typeof s.ts !== 'number' || (Date.now() - s.ts) > STASH_TTL) return false;
    show({ title: s.t, variant: s.v, qty: s.q, price: s.p, image: s.i });
    return true;
  };

  /* ── Replay on custom reload arrivals ─────────────────────────────────────
     npReplayCartToast is the single replay function (URL marker + 3-min TTL + clear-on-read →
     no spurious replay). TRIGGER differs per flow:
     - ?np_added: replayed by custom-uploader.js AFTER the design preview (#fileupload_hero)
       renders + the np-arriving overlay clears, so the toast appears ONCE, after paint (not
       over the loading spinner). NOT fired here.
     - ?rtype=another: fired here at defer time (defensive; unreachable under no-overlay, where
       refreshAll_ never runs). Safe — this defer script has defined npReplayCartToast by now. */
  if (location.search.indexOf('rtype=another') > -1) {
    window.npReplayCartToast();
  }
})();
